// Faithful JS port of the backend Mismatch.Domain/Overlays/Services/PlacementNormalizer.cs.
// Converts legacy point-anchored placements (offsetX/Y, containerW, textAnchor, maxLines)
// into the box model ({x,y,w,h,hAlign,vAlign,fit}) used by the editor. Must produce the
// same box as the backend's render-time shim, or the editor and server preview disagree.
// Idempotent: running an already-box placement through toBox only folds its (zero) offset.
//
// Unlike the backend port, toBox intentionally produces PURE box-model output — legacy
// positioning keys (offsetX/offsetY/textAnchor/containerX/containerW/maxLines) are stripped
// entirely rather than zeroed/retained. The backend keeps them around for its render-time
// shim's tolerance; the editor has no such shim, so don't "restore parity" by re-adding them.

export const DEFAULT_TEXT_W = 20 // % of 1920 — default text box width for legacy point text

const LEGACY_KEYS = ['offsetX', 'offsetY', 'textAnchor', 'containerX', 'containerW', 'maxLines']

// maxLines is the one legacy key that came back: on a POINT placement it was part of the
// positioning model and is consumed into fit + box height, so it is dropped. On a box
// placement it is the designer's own line cap and must survive the round-trip.
const stripLegacyForBox = (p) => {
  const out = stripLegacyKeys({ ...p })
  if (p.maxLines != null) out.maxLines = p.maxLines
  return out
}

const round2 = (n) => Math.round(n * 1000) / 1000

export const isPill = (p) =>
  !!(p.bgColor || p.bgWidth != null || p.bgHeight != null ||
    p.bgGradientFromField || p.bgGradientToField)

const isImage = (p) => /_logo$|_photo$/i.test(p.fieldId) || p.imageWidth != null

// Line-height as % of 1080 from font size (px). Wrap gets maxLines lines.
const textH = (fontSize = 32, lines = 1) =>
  round2((fontSize * 1.4 * Math.max(1, lines)) / 1080 * 100)

// Single-line box height (% of 1080) a given font size needs. Used to keep a
// text box tall enough for its font — otherwise the glyph overflows a short box
// and vertical alignment has no room to take effect.
export const lineHeightPct = (fontSize) => textH(fontSize, 1)

const stripLegacyKeys = (obj) => {
  for (const key of LEGACY_KEYS) delete obj[key]
  return obj
}

// Inverse of toBox at the serialization boundary (save + preview). The editor
// works in box-model top-left coords, but IMAGE placements are stored/sent in the
// legacy CENTER-anchored convention (x,y = image center) that the backend
// PlacementNormalizer and every default template already use — so the backend's
// center->top-left conversion round-trips back to exactly the editor's box.
// Text/pill placements are top-left in both editor and storage (the backend keeps
// box-model text as-is), so they pass through unchanged. Keeping images in the
// legacy convention avoids a template/DB migration.
export function fromBox(p) {
  if (isPill(p) || !isImage(p)) return p
  const w = p.imageWidth ?? 8
  const h = p.imageHeight ?? 8
  return { ...p, x: round2((p.x ?? 0) + w / 2), y: round2((p.y ?? 0) + h / 2) }
}

export function toBox(p) {
  if (isPill(p)) return p // pills unchanged

  const ax = (p.x ?? 0) + (p.offsetX ?? 0)
  const ay = (p.y ?? 0) + (p.offsetY ?? 0)
  const out = stripLegacyKeys({ ...p })

  if (isImage(p)) {
    const w = p.imageWidth ?? 8
    const h = p.imageHeight ?? 8
    out.x = round2(ax - w / 2) // was centered on the point
    out.y = round2(ay - h / 2)
    return out
  }

  // Already box-model: just fold offset (offset is legacy-only).
  if (p.w != null && p.h != null) {
    const boxed = stripLegacyForBox(p)
    boxed.x = ax
    boxed.y = ay
    return boxed
  }

  // Legacy text → box.
  const anchor = p.textAnchor ?? 'center'
  const w = p.containerW ?? DEFAULT_TEXT_W
  const lines = p.maxLines > 1 ? p.maxLines : 1
  const h = textH(p.fontSize, lines)
  out.w = w
  out.h = h
  out.hAlign = anchor
  out.vAlign = 'middle'
  out.fit = p.maxLines > 1 ? 'wrap' : 'trim'
  out.x = round2(anchor === 'left' ? ax : anchor === 'right' ? ax - w : ax - w / 2)
  out.y = round2(ay - h / 2) // was vertically centered on the point
  return out
}
